import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ICONS from "../../../../../assets/Icons";
import StatusContainer from "../../../../../components/_utils/CommonStatus/StatusContainer";
import InfoCard from "../../../../../components/_utils/CommonStatus/StatusSubComponents/StatusCardContainer/InfoCard";
import TabsWeapperSection from "../../../../../components/_utils/CommonStatus/StatusSubComponents/TabsWrapperSection";
import FormatAmount from "../../../../../components/_utils/FormatAmount/FormatAmount";
import { setHideBreadCrumb } from "../../../../../redux/features/hideBreadCrumb/hideBreadCrumb";
import { clearScheduledInternalErrors } from "../../../../../redux/features/transactions/scheduleInternalTransactions/index.js";

const ScheduleInternalMultiApprovalFailed = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [errorPageData, setErrorPageData] = useState([]);
  const [storedErrorData] = useState(() => {
    try {
      const storedData = JSON.parse(
        sessionStorage.getItem("ScheduleInternalMultiDataError") || "[]",
      );

      return Array.isArray(storedData) ? storedData : [];
    } catch {
      return [];
    }
  });

  const { scheduledInternalRejectError, scheduledInternalApproveError } =
    useSelector((state) => state.scheduleInternalTransactions);

  const actionType =
    scheduledInternalRejectError && !scheduledInternalApproveError
      ? "reject"
      : "approve";

  const findCommonNames = (transactions = [], errors = []) => {
    if (!Array.isArray(errors)) return [];

    // The error response can contain plain error messages.
    if (errors.some((item) => typeof item === "string")) {
      return errors;
    }

    const errorsByTransactionId = new Map(
      errors.map((item) => [item?.id, item?.errors]),
    );

    return transactions
      .filter((transaction) => errorsByTransactionId.has(transaction?.txn_id))
      .map((transaction) => ({
        ...transaction,
        errors: errorsByTransactionId.get(transaction?.txn_id),
      }));
  };

  const filterErrorTxn = findCommonNames(errorPageData, storedErrorData);

  useEffect(() => {
    dispatch(setHideBreadCrumb({ hide: true }));

    try {
      const storedData = JSON.parse(
        sessionStorage.getItem("intSelectedScheduleCard") || "[]",
      );

      const selectedDetails = storedData?.[0]?.selectedDetails;

      setErrorPageData(Array.isArray(selectedDetails) ? selectedDetails : []);
    } catch {
      setErrorPageData([]);
    }

    return () => {
      dispatch(setHideBreadCrumb({ hide: false }));
      dispatch(clearScheduledInternalErrors());
    };
  }, [dispatch]);

  const goToDashboard = () => {
    navigate("/internal_transaction_dashboard");
  };

  const buttonRows = [
    [
      {
        btnText: "Go to dashboard",
        enabledBgColor: "var(--bg-positive-bg)",
        enabledTextColor: "var(--text-primary)",
        icon: ICONS?.DASHBOARD,
        iconToBeAlign: "left",
        fullWidthRequired: true,
        outLineColor: "var(--text-primary)",
        hoverOption: "dark",
        handleFunction: goToDashboard,
      },
    ],
  ];

  const getTitle = () =>
    actionType === "reject"
      ? "Oops! Schedules rejection failed"
      : "Oops! Schedules approval failed";

  const getSubtitle = () => {
    const action = actionType === "reject" ? "rejection" : "approval";

    return `An error occurred with the following schedules, causing the ${action} of all the schedules to fail.`;
  };

  const getErrorMessage = (error) => {
    if (typeof error === "string") return error;

    return (
      error?.reason || error?.message || "No specific error details found."
    );
  };

  // Prepare a single list of error cards with stable, data-based keys.
  const errorCardItems = (() => {
    const usedKeys = new Map();

    const createErrorItem = (baseKey, message) => {
      const occurrence = usedKeys.get(baseKey) || 0;
      usedKeys.set(baseKey, occurrence + 1);

      return {
        key: occurrence === 0 ? baseKey : `${baseKey}-${occurrence}`,
        message,
      };
    };

    if (!Array.isArray(filterErrorTxn)) return [];

    return filterErrorTxn.flatMap((item) => {
      if (typeof item === "string") {
        return [createErrorItem(`message:${item}`, item)];
      }

      if (Array.isArray(item?.errors) && item.errors.length > 0) {
        return item.errors.map((error) => {
          const message = getErrorMessage(error);
          const transactionId =
            item?.txn_id ?? item?.id ?? item?.scheduled_name ?? "schedule";

          const errorId =
            error?.error_id ?? error?.id ?? error?.code ?? message;

          return createErrorItem(
            `transaction:${transactionId}:error:${String(errorId)}`,
            message,
          );
        });
      }

      const transactionId =
        item?.txn_id ?? item?.id ?? item?.scheduled_name ?? "unknown";

      return [
        createErrorItem(
          `transaction:${transactionId}:fallback`,
          "No specific error details found.",
        ),
      ];
    });
  })();

  return (
    <StatusContainer
      status="error"
      buttonRows={buttonRows}
      title={getTitle()}
      subtitle={getSubtitle()}
    >
      <>
        <TabsWeapperSection title="Error details">
          {errorCardItems.map((errorItem) => (
            <InfoCard
              key={errorItem.key}
              bgColor="var(--negative-card)"
              rows={[
                {
                  leftTitle: errorItem.message,
                  leftSubTitle: "Error message",
                },
              ]}
            />
          ))}
        </TabsWeapperSection>

        <TabsWeapperSection title="Schedules Details">
          {errorPageData.map((transaction) => {
            if (!transaction?.txn_id) return null;

            const scheduleDetailPath = `/internal_transaction_dashboard/schedule_view_all/schedule_detail_page/${transaction.txn_id}`;

            return (
              <InfoCard
                key={transaction.txn_id}
                bgColor="var(--negative-card)"
                hoverable="white"
                onClick={() => navigate(scheduleDetailPath)}
                rows={[
                  {
                    leftTitle: transaction?.scheduled_name,
                    leftSubTitle: "Schedule name",
                    hasRight: true,
                    rightText: true,
                    rightTitleTextColor: "var(--text-primary)",
                    rightTitle: (
                      <>
                        INR <FormatAmount price={transaction?.amount} />
                      </>
                    ),
                    rightTextFontWeight: "semibold",
                    rightSubTitle: "Amount",
                    rightIcon: true,
                  },
                ]}
              />
            );
          })}
        </TabsWeapperSection>
      </>
    </StatusContainer>
  );
};

export default ScheduleInternalMultiApprovalFailed;
